import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { getBlog, px } from "../lib/api";
import { Reveal } from "../components/motion";

export default function BlogArticle() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBlog(null);
    setNotFound(false);
    getBlog(slug)
      .then(setBlog)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-5" data-testid="blog-article-notfound">
        <p className="font-heading text-5xl uppercase">Article not found</p>
        <Link to="/blog" className="text-orange font-bold uppercase mt-4">Back to Journal</Link>
      </div>
    );
  }

  if (!blog) {
    return <div className="min-h-screen" />;
  }

  return (
    <article data-testid="blog-article" className="pb-32">
      <div className="relative pt-32 pb-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-cream/60 hover:text-orange transition-colors mb-6 text-sm font-bold uppercase">
            <ArrowLeft className="w-4 h-4" /> The Journal
          </Link>
          <p className="text-orange font-bold uppercase text-sm tracking-widest">{blog.category} · {blog.read}</p>
          <h1 className="font-heading uppercase text-4xl sm:text-6xl tracking-tighter leading-[0.9] mt-3">{blog.title}</h1>
          <p className="text-cream/50 text-sm mt-4">{blog.date}</p>
        </div>
      </div>

      <Reveal className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="rounded-lg overflow-hidden border border-border shadow-hard mb-10">
          <img src={px(blog.image, 1400)} alt={blog.title} className="w-full aspect-[16/9] object-cover" />
        </div>
      </Reveal>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-3xl mx-auto px-5 sm:px-8"
      >
        <p className="text-cream/85 text-xl leading-relaxed first-letter:font-heading first-letter:text-6xl first-letter:text-orange first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8]">
          {blog.body}
        </p>
        <p className="text-cream/70 text-lg leading-relaxed mt-6">
          Every plate we serve carries this philosophy. It’s slower, it’s harder, and it’s exactly why our regulars keep coming back. Quality isn’t a feature — it’s the whole point.
        </p>

        <div className="mt-12 rounded-lg bg-orange text-primary-foreground p-8 text-center">
          <p className="font-heading uppercase text-3xl tracking-tight">Hungry after reading?</p>
          <Link to="/menu" data-testid="article-order-cta" className="inline-block mt-4 bg-brown text-cream font-bold uppercase px-8 py-3.5 rounded-md hover:scale-105 transition-transform">
            Order the Menu
          </Link>
        </div>
      </motion.div>
    </article>
  );
}
